import type { ReactElement } from 'react';
import {
    BedrockIcon,
    ClaudeIcon,
    CopilotIcon,
    DefaultProviderIcon,
    EntraIdIcon,
    AzureAiFoundryIcon,
    GeminiIcon,
    GitHubCopilotIcon,
    GoogleWorkspaceIcon,
    GrokIcon,
    OktaIcon,
    OpenAIIcon,
    PingIdentityIcon,
    VertexAiIcon,
} from '@/shared/icons/providers';
import { AIProvider } from '@/shared/types/ProviderTypes';

/**
 * Resolve a provider icon from a backend `providerTypeId` (e.g. `aws-bedrock/...`).
 * Returns `null` when no icon is registered for the type — callers that wrap
 * the icon in a container can use this to skip the wrapper entirely.
 */
export function providerIconForType(providerTypeId: string, size = 32): ReactElement | null {
    if (providerTypeId.startsWith('aws-bedrock/')) return <BedrockIcon size={size} />;
    if (providerTypeId.startsWith('gcp-vertex/')) return <VertexAiIcon size={size} />;
    if (providerTypeId.startsWith('microsoft-copilot/')) return <CopilotIcon size={size} />;
    if (providerTypeId.startsWith('github-copilot/')) return <GitHubCopilotIcon size={size} />;
    if (providerTypeId.startsWith('openai/')) return <OpenAIIcon size={size} />;
    if (providerTypeId.startsWith('google-vault/')) return <GeminiIcon size={size} />;
    if (providerTypeId.startsWith('google-workspace/')) return <GoogleWorkspaceIcon size={size} />;
    if (providerTypeId.startsWith('entra-id/')) return <EntraIdIcon size={size} />;
    if (providerTypeId.startsWith('okta/')) return <OktaIcon size={size} />;
    if (providerTypeId.startsWith('ping-identity/')) return <PingIdentityIcon size={size} />;
    if (providerTypeId.startsWith('anthropic/')) return <ClaudeIcon size={size} />;
    if (providerTypeId.startsWith('xai/')) return <GrokIcon size={size} />;
    if (providerTypeId.startsWith('azure-ai-foundry/')) return <AzureAiFoundryIcon size={size} />;
    return null;
}

function providerIconForEnum(provider: AIProvider, size: number): ReactElement {
    switch (provider) {
        case AIProvider.GEMINI:
            return <GeminiIcon size={size} />;
        case AIProvider.OPENAI:
            return <OpenAIIcon size={size} />;
        case AIProvider.CLAUDE:
            return <ClaudeIcon size={size} />;
        case AIProvider.COPILOT:
            return <CopilotIcon size={size} />;
        default:
            return <DefaultProviderIcon size={size} />;
    }
}

interface ProviderIconByTypeProps {
    providerTypeId: string;
    size?: number;
    className?: string;
}

interface ProviderIconByProviderProps {
    provider: AIProvider;
    size?: number;
    className?: string;
}

type ProviderIconProps = ProviderIconByTypeProps | ProviderIconByProviderProps;

/**
 * Renders a provider icon by either backend `providerTypeId` (real connection
 * data, delegated to {@link providerIconForType}) or `AIProvider` enum (legacy
 * mock-data callers). The `providerTypeId` variant returns `null` when no icon
 * is registered; the enum variant always renders, falling back to a default icon.
 */
export default function ProviderIconComponent(props: ProviderIconProps): ReactElement | null {
    const size = props.size ?? 16;
    if ('providerTypeId' in props) {
        return providerIconForType(props.providerTypeId, size);
    }
    return providerIconForEnum(props.provider, size);
}
